import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Text, Button, Group, Select, NumberInput, Textarea, Card, Loader, Center } from '@mantine/core'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { ReloaderRecipeFormValues } from '../../app/models/reloaderRecipe'
import { CartridgeSpecDto } from '../../app/models/ammo'
import MyTextInput from '../../app/common/form/MyTextInput'

const validationSchema = Yup.object({
  cartridgeSpecId: Yup.string().required('Cartridge is required'),
  powderBrand: Yup.string().required('Powder brand is required'),
  powderType: Yup.string().required('Powder type is required'),
  chargeWeightGrMin: Yup.number().required('Charge weight is required').positive(),
  chargeWeightGrMax: Yup.number().required('Max charge weight is required').positive(),
  bulletBrand: Yup.string().required('Bullet brand is required'),
  bulletWeightGr: Yup.number().required('Bullet weight is required').positive(),
  bulletType: Yup.string().required('Bullet type is required'),
  oalIn: Yup.number().required('OAL is required').positive(),
  intendedUse: Yup.string().required('Intended use is required')
})

const BULLET_TYPES = [
  { value: 'FMJ', label: 'FMJ (Full Metal Jacket)' },
  { value: 'JHP', label: 'JHP (Jacketed Hollow Point)' },
  { value: 'SP', label: 'SP (Soft Point)' },
  { value: 'HPBT', label: 'HPBT (Hollow Point Boat Tail)' },
  { value: 'other', label: 'Other' }
]

export default function SubmitRecipePage () {
  const history = useHistory()
  const { userStore } = useStore()
  const [cartridges, setCartridges] = useState<CartridgeSpecDto[]>([])
  const [loadingCartridges, setLoadingCartridges] = useState(true)

  useEffect(() => {
    const loadCartridges = async () => {
      try {
        const result = await agent.CartridgeSpecs.list({ pageSize: 100 })
        setCartridges(result.items)
      } catch (error) {
        toast.error('Failed to load cartridges')
      } finally {
        setLoadingCartridges(false)
      }
    }
    loadCartridges()
  }, [])

  const canSubmit = userStore.user && userStore.user.ammoScore >= 50

  if (!canSubmit) {
    return (
      <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 600, margin: '0 auto', padding: 24 }}>
        <Card padding="lg" withBorder>
          <Text size="lg" weight={600} mb="md">Recipe Submissions Require Contributor Tier</Text>
          <Text size="sm" color="dimmed" mb="md">
            Recipe submissions require at least 50 AmmoScore to ensure community submissions meet safety standards.
          </Text>
          <Text size="sm" mb="md">
            Your current score: <strong>{userStore.user?.ammoScore ?? 0}</strong>
          </Text>
          <Text size="sm" color="dimmed">
            Keep contributing through posts, reviews, and votes to unlock this feature.
          </Text>
        </Card>
      </Box>
    )
  }

  if (loadingCartridges) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  const handleSubmit = async (values: ReloaderRecipeFormValues) => {
    try {
      await agent.ReloaderRecipes.create(values)
      toast.success('Recipe submitted! It will appear after a brief review.')
      history.push('/ammo/reloading')
    } catch (error) {
      toast.error('Failed to submit recipe')
    }
  }

  return (
    <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 720, margin: '0 auto', padding: 24 }}>
      <Button variant="subtle" onClick={() => history.push('/ammo/reloading')} sx={{ paddingLeft: 0, marginBottom: 16 }}>
        ← Back to Reloader Forum
      </Button>

      <Text sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 600, marginBottom: 4 }}>
        POST A RECIPE
      </Text>
      <Text size="sm" color="dimmed" mb="lg">
        Share your reloading recipe with the community. Include actual test data for best results.
      </Text>

      <Card padding="lg" sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)' }}>
        <Formik
          initialValues={{
            cartridgeSpecId: '',
            powderBrand: '',
            powderType: '',
            chargeWeightGrMin: 0,
            chargeWeightGrMax: 0,
            primerBrand: '',
            primerType: '',
            brassBrand: '',
            bulletBrand: '',
            bulletWeightGr: 0,
            bulletType: '',
            oalIn: 0,
            coalIn: undefined,
            seatingDepthIn: undefined,
            estimatedVelocityFps: undefined,
            chronoData: '',
            intendedUse: 'target',
            notes: ''
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, isSubmitting, isValid, values, setFieldValue, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <Select
                label="Cartridge *"
                placeholder="Select cartridge"
                searchable
                required
                value={values.cartridgeSpecId}
                onChange={(v) => setFieldValue('cartridgeSpecId', v || '')}
                data={cartridges.map(c => ({ value: c.id, label: c.name }))}
                error={touched.cartridgeSpecId && errors.cartridgeSpecId}
                mb="md"
              />

              <Text weight={600} size="sm" mt="lg" mb="sm">Powder Specifications</Text>
              <MyTextInput name="powderBrand" label="Brand *" placeholder="Hodgdon" />
              <MyTextInput name="powderType" label="Type *" placeholder="Varget" />
              <Group grow mb="md">
                <NumberInput
                  label="Min Charge (gr) *"
                  placeholder="25.5"
                  value={values.chargeWeightGrMin}
                  onChange={(v) => setFieldValue('chargeWeightGrMin', v || 0)}
                  error={touched.chargeWeightGrMin && errors.chargeWeightGrMin}
                />
                <NumberInput
                  label="Max Charge (gr) *"
                  placeholder="26.5"
                  value={values.chargeWeightGrMax}
                  onChange={(v) => setFieldValue('chargeWeightGrMax', v || 0)}
                  error={touched.chargeWeightGrMax && errors.chargeWeightGrMax}
                />
              </Group>

              <Text weight={600} size="sm" mt="lg" mb="sm">Primer Specifications</Text>
              <MyTextInput name="primerBrand" label="Brand" placeholder="CCI" />
              <MyTextInput name="primerType" label="Type" placeholder="400 (Small Rifle)" mb="md" />

              <Text weight={600} size="sm" mt="lg" mb="sm">Brass & Bullet</Text>
              <MyTextInput name="brassBrand" label="Brass Brand (optional)" placeholder="Federal" />
              <MyTextInput name="bulletBrand" label="Bullet Brand *" placeholder="Speer" />
              <Group grow mb="md">
                <NumberInput
                  label="Bullet Weight (gr) *"
                  placeholder="55"
                  value={values.bulletWeightGr}
                  onChange={(v) => setFieldValue('bulletWeightGr', v || 0)}
                  error={touched.bulletWeightGr && errors.bulletWeightGr}
                />
                <Select
                  label="Bullet Type *"
                  placeholder="FMJ"
                  value={values.bulletType}
                  onChange={(v) => setFieldValue('bulletType', v || '')}
                  data={BULLET_TYPES}
                  error={touched.bulletType && errors.bulletType}
                />
              </Group>

              <Text weight={600} size="sm" mt="lg" mb="sm">Dimensions</Text>
              <Group grow mb="md">
                <NumberInput
                  label="OAL (inches) *"
                  placeholder="2.260"
                  value={values.oalIn}
                  onChange={(v) => setFieldValue('oalIn', v || 0)}
                  error={touched.oalIn && errors.oalIn}
                  precision={3}
                  step={0.001}
                />
                <NumberInput
                  label="COAL (inches)"
                  placeholder="2.500"
                  value={values.coalIn}
                  onChange={(v) => setFieldValue('coalIn', v || undefined)}
                  precision={3}
                  step={0.001}
                />
              </Group>
              <NumberInput
                label="Seating Depth (inches)"
                placeholder="0.125"
                value={values.seatingDepthIn}
                onChange={(v) => setFieldValue('seatingDepthIn', v || undefined)}
                precision={3}
                step={0.001}
                mb="md"
              />

              <Text weight={600} size="sm" mt="lg" mb="sm">Performance Data</Text>
              <NumberInput
                label="Estimated Velocity (fps)"
                placeholder="3050"
                value={values.estimatedVelocityFps}
                onChange={(v) => setFieldValue('estimatedVelocityFps', v || undefined)}
                mb="md"
              />
              <Textarea
                label="Chrono Data (optional)"
                placeholder="Paste actual velocity readings here..."
                value={values.chronoData || ''}
                onChange={(e) => setFieldValue('chronoData', e.currentTarget.value)}
                rows={4}
                mb="md"
              />

              <Text weight={600} size="sm" mt="lg" mb="sm">Additional Info</Text>
              <Select
                label="Intended Use *"
                placeholder="Select use case"
                value={values.intendedUse}
                onChange={(v) => setFieldValue('intendedUse', v || 'target')}
                data={[
                  { value: 'target', label: 'Target' },
                  { value: 'hunting', label: 'Hunting' },
                  { value: 'defense', label: 'Defense' },
                  { value: 'plinking', label: 'Plinking' }
                ]}
                mb="md"
              />
              <Textarea
                label="Notes (optional)"
                placeholder="Any special notes or observations?"
                value={values.notes || ''}
                onChange={(e) => setFieldValue('notes', e.currentTarget.value)}
                rows={3}
                mb="lg"
              />

              <Group position="right">
                <Button variant="light" onClick={() => history.push('/ammo/reloading')}>
                  Cancel
                </Button>
                <Button type="submit" color="ember" disabled={isSubmitting || !isValid} loading={isSubmitting}>
                  Submit Recipe
                </Button>
              </Group>
            </Form>
          )}
        </Formik>
      </Card>
    </Box>
  )
}
